import { useEffect, useState } from 'react'
import { Divider } from 'components/divider'
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { EyeIcon, ArrowLeftIcon, Cog8ToothIcon, EllipsisVerticalIcon, PencilIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from 'components/dropdown'
import { useNavigate, useLocation } from 'react-router-dom'
import { GET_SYSTEM_CONFIG } from 'graphql/common/queries'
import { useLazyQuery, useMutation } from '@apollo/client'
import loadingImage from '../../assets/images/boat-loading.gif'
import { UPDATE_SYSTEM_CONFIG } from 'graphql/common/mutations'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { ConfigServicePayment } from './components/servicePayments'
import { ConfigDiscounts } from './components/discounts'
import { t } from 'i18next'

const tabs = [
    { label: t('General Settings'), name: 'General Settings', href: '#', current: true },
    { label: t('Service Payments'), name: 'Service Payments', href: '#', current: false },
    { label: t('Discounts'), name: 'Discounts', href: '#', current: false },
    { label: t('Recipient Parties'), name: 'Recipient Parties', href: '#', current: false },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function SystemConfig() {
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const { t } = useTranslation()
    const { state } = useLocation()
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [activeTab, setActiveTab] = useState(() => {
        // First try to get from localStorage
        const savedTab = localStorage.getItem('systemConfigActiveTab')
        // Then check router state
        if (savedTab) return savedTab
        // Fall back to default tab from tabs array
        return tabs.find(tab => tab.current)?.name
    })
    const [configList, setConfigList] = useState(null)
    const [selectedConfig, setSelectedConfig] = useState(null)

    useEffect(() => {
        // If state has tab info, use it and update localStorage
        if (state?.tab) {
            setActiveTab(state.tab)
            localStorage.setItem('systemConfigActiveTab', state.tab)
        }
    }, [state])

    const handleTabClick = tabName => {
        setActiveTab(tabName)
        // Save to localStorage when tab changes
        localStorage.setItem('systemConfigActiveTab', tabName)
    }
    const [getConfigList, { loading: configListLoading, error: configListError }] = useLazyQuery(GET_SYSTEM_CONFIG, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getSystemConfigs.success && data.getSystemConfigs.code === '200') {
                setConfigList(data.getSystemConfigs.system_configs)
            }
        },
    })
    // Search configs Config List
    const getSystemConfig = () => {
        getConfigList({
            variables: {
                settingName: null,
            },
        })
    }
    useEffect(() => {
        getSystemConfig()
    }, [])

    const handleEditSystemConfig = (e, config) => {
        console.log(config)
        e.preventDefault()
        e.stopPropagation()
        setSelectedConfig(config)
        setOpen(true)
    }
    const handleDialogClose = () => {
        setOpen(false)
        setShowSucDialog(false)

        setShowErrDialog(false)
    }
    // Mutation Update config
    const [updateSystemConfig, { loading: updateLoading }] = useMutation(UPDATE_SYSTEM_CONFIG, {
        onCompleted: data => {
            if (data?.updateSystemConfig.code === '200' && data?.updateSystemConfig.success === true) {
                setShowSucDialog(true)
                setTimeout(() => {
                    setShowSucDialog(false)
                    setOpen(false)
                    getSystemConfig()
                }, 500)
            } else {
                setDialogMessage(data?.updateSystemConfig.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessage(error.message)
            setShowErrDialog(true)
        },
    })
    const handleSaveSystemConfig = () => {
        setOpen(false)
        if (selectedConfig === null || selectedConfig === undefined || selectedConfig === '') {
            return
        }
        console.log({
            settingValue: {
                description: selectedConfig.setting_value.description,
                value: selectedConfig.setting_value.value,
            },
            settingName: selectedConfig.setting_name,
        })
        updateSystemConfig({
            variables: {
                settingValue: {
                    description: selectedConfig.setting_value.description,
                    value: selectedConfig.setting_value.value,
                },
                settingName: selectedConfig.setting_name,
            },
        })
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between"></div>
                    <div className="flex flex-wrap items-center sm:flex-nowrap">
                        <div className="mx-auto flex flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                            <Cog8ToothIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3"> {t('System Configuration')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                {t('Easily manage the system configurations.')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <Divider />
            <div>
                <div className="sm:hidden">
                    <select
                        id="tabs"
                        name="tabs"
                        value={activeTab}
                        onChange={e => handleTabClick(e.target.value)}
                        className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    >
                        {tabs.map(tab => (
                            <option key={tab.name}>{tab.label}</option>
                        ))}
                    </select>
                </div>
                <div className="hidden sm:block">
                    <div className="border-b border-gray-200">
                        <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                            {tabs.map(tab => (
                                <button
                                    key={tab.name}
                                    onClick={() => handleTabClick(tab.name)}
                                    className={classNames(
                                        tab.name === activeTab
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                        'whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium'
                                    )}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            {activeTab === 'General Settings' && (
                <>
                    {configListLoading && (
                        <div className="flex flex-col justify-center items-center bg-white h-96 mt-16 w-full">
                            <img alt="logo" src={loadingImage} className="w-[250px]" />
                            <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                        </div>
                    )}
                    {configListError && (
                        <div className="flex justify-center items-center bg-white py-5">
                            <div className="text-lg text-center">{t('Failed to fetch boats!')}</div>
                        </div>
                    )}
                    {!configListLoading && !configListError && configList?.length === 0 && (
                        <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                            <svg
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                className="mx-auto h-12 w-12 text-gray-400"
                            >
                                <path
                                    d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                    strokeWidth={2}
                                    vectorEffect="non-scaling-stroke"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                            <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                            <div className="mt-6"></div>
                        </div>
                    )}

                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex mt-2">
                        {!configListLoading && !configListError && configList?.length > 0 && (
                            <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{t('Key')}</TableHeader>
                                        <TableHeader>{t('Description')}</TableHeader>
                                        <TableHeader>{t('Value')}</TableHeader>
                                        <TableHeader>{t('Action')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {configList?.map(sconfig => (
                                        <TableRow
                                            className="cursor-pointer hover:bg-gray-50"
                                            onClick={e => {
                                                handleEditSystemConfig(e, sconfig)
                                            }}
                                            key={sconfig.setting_name}
                                        >
                                            <TableCell>{sconfig.setting_name}</TableCell>
                                            <TableCell>{sconfig.setting_value.description}</TableCell>
                                            <TableCell>{sconfig.setting_value.value}</TableCell>
                                            <TableCell>
                                                <Dropdown>
                                                    <DropdownButton
                                                        plain
                                                        aria-label="More options"
                                                        onClick={e => e.stopPropagation()}
                                                        className="cursor-pointer"
                                                    >
                                                        <EllipsisVerticalIcon />
                                                    </DropdownButton>
                                                    <DropdownMenu anchor="bottom">
                                                        <DropdownItem
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                handleEditSystemConfig(e, sconfig)
                                                            }}
                                                        >
                                                            <PencilIcon />
                                                            <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                        </DropdownItem>
                                                    </DropdownMenu>
                                                </Dropdown>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </>
            )}
            {activeTab === 'Service Payments' && <ConfigServicePayment />}
            {activeTab === 'Discounts' && (
                <>
                    <ConfigDiscounts />
                </>
            )}
            {activeTab === 'Recipient Parties' && (
                <div className="mt-4">
                    <button
                        onClick={() => navigate('/system-config/recipient-parties')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary/[0.9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                        {t('Manage Recipient Parties')}
                    </button>
                </div>
            )}
            <Dialog open={open} onClose={setOpen} className="relative z-10">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="">
                                    <div className="">
                                        <div className="bg-white px-4 pb-4 pt-5 sm:p-0 sm:pb-4">
                                            <div className="px-4 sm:px-0">
                                                <h3 className="text-lg font-bold leading-7 text-gray-900 text-primary ps-2">
                                                    {t('Edit System Configuration')}
                                                </h3>
                                            </div>
                                            <div className="mt-6 border-t border-gray-100">
                                                <dl className="divide-y divide-gray-100">
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Key')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            <div className="mt-2">
                                                                <input
                                                                    id="key-name"
                                                                    name="key-name"
                                                                    type="text"
                                                                    autoComplete="given-name"
                                                                    value={selectedConfig?.setting_name}
                                                                    onChange={e =>
                                                                        setSelectedConfig({
                                                                            ...selectedConfig,
                                                                            setting_name: e.target.value,
                                                                        })
                                                                    }
                                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                                                />
                                                            </div>
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Value')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            <div className="mt-2">
                                                                <input
                                                                    id="config-value"
                                                                    name="config-value"
                                                                    type="number"
                                                                    value={selectedConfig?.setting_value.value}
                                                                    onChange={e =>
                                                                        setSelectedConfig({
                                                                            ...selectedConfig,
                                                                            setting_value: {
                                                                                ...selectedConfig.setting_value,
                                                                                value: parseFloat(e.target.value),
                                                                            },
                                                                        })
                                                                    }
                                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                                                />
                                                            </div>
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Description')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            <div className="mt-2">
                                                                <textarea
                                                                    id="description"
                                                                    name="description"
                                                                    rows={3}
                                                                    value={selectedConfig?.setting_value.description}
                                                                    onChange={e =>
                                                                        setSelectedConfig({
                                                                            ...selectedConfig,
                                                                            setting_value: {
                                                                                ...selectedConfig.setting_value,
                                                                                description: e.target.value,
                                                                            },
                                                                        })
                                                                    }
                                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                                                />
                                                            </div>
                                                        </dd>
                                                    </div>
                                                </dl>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        data-autofocus
                                        onClick={() => setOpen(false)}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSaveSystemConfig()}
                                        data-autofocus
                                        className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-primary hover:bg-primary/[0.9]`}
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={dialogMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={dialogMessage} onClose={handleDialogClose} />
            )}
            {updateLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
